import Totals from "../crew/Totals";
import BigNum from "../math/BigNum";
import GameTime from "../time/GameTime";

export default class Bonus {
    /**
     * @param {string} id 
     * @param {string} title 
     * @param {string} description 
     * @param {string} spriteFileName 
     * @param {GameTime} gameTime
     * @param {Totals} totals
     * @param {BigNum} cost
     * @param {number} activationTurns
     */
    constructor(id, title, description, spriteFileName, gameTime, totals, cost, activationTurns) {
        /**
         * @constant
         * @type {string}
         */
        this.id = id;
        /**
         * @constant
         * @type {string}
         */
        this.title = title;
        /**
         * @constant
         * @type {string}
         */
        this.description = description;
        /**
         * @constant
         * @type {string}
         */
        this.spriteFileName = spriteFileName;
        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = gameTime;
        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;
        /**
         * @constant
         * @type {BigNum}
         */
        this.cost = cost;
        /**
         * @constant
         * @type {number}
         */
        this.activationTurns = activationTurns;


        /**
         * @type {boolean}
         */
        this.activated = false;
        /**
         * @type {number}
         */
        this.startTurn = 0;
    }

    /**
     * @returns {string}
     */
    getTitle() {
        return this.title;
    }
    /**
     * @returns {string}
     */
    getDescription() {
        return this.description;
    }

    /**
     * @override
     * @returns {boolean}
     */
    isAffordable() {
        return this.cost.lessThanEquals(this.totals.gold);
    }

    /**
     * @override
     * @returns {boolean}
     */
    isPurchasable() {
        if (this.activated) {
            return false;
        }
        if (!this.isAffordable()) {
            return false;
        }

        return true;
    }

    onPurchase() {
        if (!this.isPurchasable()) {
            return;
        }
        
        this.totals.decrementGold(this.cost);
        this.onActivation();
    }


    /**
     * When activated, this return the percent of the activation period completed.
     * @returns {number}
     */
    getActivationPercent() {
        if (!this.activated) {
            return 0;
        }

        const elapsedTurns = this.gameTime.turnNumber - this.startTurn;
        const ratio = Math.min(1.0, elapsedTurns / this.activationTurns);
        return ratio * 100;
    }

    /**
     * @returns {boolean}
     */
    onActivation() {
        if (this.activated) {
            return false;
        }

        this.startTurn = this.gameTime.turnNumber;
        this.activated = true;
        return true;
    }

    onActivationFromSave() {
        this.activated = true;
        // Don't mess with the start turn.
    }

    /**
     * Sub-classed should implement this if they have things they need to tear down.
     * @returns {boolean}
     */
    onDeactivation() {
        if (!this.activated) {
            return false;
        }

        this.activated = false;
        return true;
    }

    /**
     */
    updateForTurn() {
        if (this.activated) {
            const turnNumber = this.gameTime.turnNumber;

            // See if the skill is still active.
            this.activated = turnNumber - this.startTurn < this.activationTurns;

            // If the skill has stopped being active, then start the cool down process.
            if (!this.activated) {
                this.onDeactivation();
            }
        }
    }

    /**
     * Returns a number value that represents the bonus when activated.
     * This is used by the save logic.
     * @abstract
     * @returns {number}
     */
    getActivationValue() {
    }

    /**
     * Sub-classes must override this.
     * @abstract
     */
    reset() {
        this.activated = false;
        this.startTurn = 0;
    }    
}